import React, { useState, useEffect, useCallback } from "react";
import {
    ChevronRight,
    ShieldCheck,
    History,
    Lock,
    BadgeCheck,
    Tag,
    CreditCard,
    Loader2,
    AlertTriangle,
    CheckCircle,
    Clock
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useConsultantSubscriptionStore from "../../store/consultant/useConsultantSubscriptionStore";
import useConsultantAuthStore from "../../store/consultant/useConsultantAuthStore";
import { toast } from "react-hot-toast";
import { load } from "@cashfreepayments/cashfree-js";
import ConsultantLayout from "../../layout/consultant/ConsultantLayout";

const ConsultantSubscription = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { consultant, getProfile } = useConsultantAuthStore();
    const { offer, isLoading, fetchOffer, createOrder, verifyPayment } = useConsultantSubscriptionStore();
    const [cashfree, setCashfree] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(false);

    useEffect(() => {
        const checkAuthAndFetchData = async () => {
            const token = localStorage.getItem("consultantToken");
            if (!token) {
                toast.error("Please login to access subscription");
                navigate("/consultant/login");
                return;
            }

            // Fetch all required data
            await Promise.all([
                fetchOffer(),
                getProfile(),
                initializeCashfree()
            ]);
        };

        checkAuthAndFetchData();
    }, []);

    const initializeCashfree = async () => {
        try {
            const mode = process.env.REACT_APP_CASHFREE_MODE || "production";
            const cf = await load({ mode });
            setCashfree(cf);
        } catch (error) {
            console.error("Failed to load Cashfree SDK:", error);
            toast.error("Payment system initialization failed");
        }
    };

    // Handle redirect with order_id
    useEffect(() => {
        const orderId = searchParams.get("order_id");
        if (orderId) {
            verifyPaymentCallback(orderId);
            // Clean URL
            navigate(window.location.pathname, { replace: true });
        }
    }, [searchParams]);

    const verifyPaymentCallback = async (orderId) => {
        if (processingPayment) return; // Prevent double trigger

        const verifyToastId = toast.loading("Verifying payment...");
        try {
            const result = await verifyPayment(orderId);
            toast.dismiss(verifyToastId);

            if (result) {
                toast.success("Subscription activated successfully!");
                // Give it a moment before redirecting
                setTimeout(() => {
                    navigate("/consultant/dashboard");
                }, 1500);
            } else {
                toast.error("Payment verification failed. Please check your history.");
            }
        } catch (error) {
            toast.dismiss(verifyToastId);
            toast.error("Verification error occurred.");
        }
    };

    const handlePayNow = async () => {
        if (!offer) {
            toast.error("No subscription offer available");
            return;
        }

        if (!cashfree) {
            toast.error("Payment system not ready. Please refresh.");
            return;
        }

        let consultantProfileId = consultant?.profileId || consultant?.profile_id || consultant?.consultant_profile_id;

        // If not found, try one last refresh
        if (!consultantProfileId) {
            toast.loading("Refreshing profile data...", { id: "refresh-profile" });
            const freshProfile = await getProfile();
            toast.dismiss("refresh-profile");
            consultantProfileId = freshProfile?.profileId || freshProfile?.profile_id || freshProfile?.consultant_profile_id;
        }

        if (!consultantProfileId) {
            toast.error("Consultant profile not found. Please try logging in again.");
            return;
        }

        setProcessingPayment(true);
        try {
            const cgstR = offer?.cgst_percentage ?? (offer?.gst_percentage ? offer.gst_percentage / 2 : 0);
            const sgstR = offer?.sgst_percentage ?? (offer?.gst_percentage ? offer.gst_percentage / 2 : 0);
            const totalGstRate = (Number(cgstR) + Number(sgstR)) / 100;
            const bAmount = offer?.amount ? parseFloat(offer.amount) : 0;
            const totalAmount = Number((bAmount * (1 + totalGstRate)).toFixed(2));
            const returnUrl = `${window.location.origin}/consultant/subscription`;

            console.log("[DEBUG] handlePayNow values:", {
                consultantProfileId,
                baseAmount: bAmount,
                totalGstRate,
                totalAmount,
                offer
            });

            if (isNaN(totalAmount) || totalAmount <= 0) {
                toast.error("Invalid total amount calculated.");
                setProcessingPayment(false);
                return;
            }

            const orderDetails = await createOrder(consultantProfileId, totalAmount, returnUrl);

            if (!orderDetails || !orderDetails.payment_session_id) {
                setProcessingPayment(false);
                return;
            }

            const checkoutOptions = {
                paymentSessionId: orderDetails.payment_session_id,
                redirectTarget: "_self" // Changed from _modal to allow proper return flow
            };

            cashfree.checkout(checkoutOptions).then((result) => {
                if (result.error) {
                    console.error("Cashfree checkout error:", result.error);
                    toast.error(result.error.message || "Payment failed");
                    setProcessingPayment(false);
                }
            });
        } catch (error) {
            console.error("Payment error:", error);
            toast.error("Payment initiation failed");
            setProcessingPayment(false);
        }
    };

    const formatCurrency = (val) => {
        if (val === null || val === undefined || isNaN(val)) return "₹0.00";
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR"
        }).format(val);
    };

    const isPageLoading = isLoading && !offer;
    const isTransacting = processingPayment || (isLoading && !!offer);

    // Calculations — use dynamic CGST/SGST rates from admin config
    const baseAmount = offer ? parseFloat(offer.amount) : 0;
    const cgstRate = offer?.cgst_percentage ?? (offer?.gst_percentage ? offer.gst_percentage / 2 : 0);
    const sgstRate = offer?.sgst_percentage ?? (offer?.gst_percentage ? offer.gst_percentage / 2 : 0);
    const cgstAmount = Number((baseAmount * (cgstRate / 100)).toFixed(2));
    const sgstAmount = Number((baseAmount * (sgstRate / 100)).toFixed(2));
    const totalGst = Number((cgstAmount + sgstAmount).toFixed(2));
    const total = Number((baseAmount + totalGst).toFixed(2));

    if (isPageLoading) {
        return (
            <ConsultantLayout>
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                    <Loader2 className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin" />
                    <p className="mt-4 text-gray-500 font-medium">Fetching subscription details...</p>
                </div>
            </ConsultantLayout>
        );
    }

    if (!offer && !isLoading) {
        return (
            <ConsultantLayout>
                <div className="p-6 max-w-4xl mx-auto">
                    <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
                        <AlertTriangle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800">No active plans</h2>
                        <p className="text-gray-500 mt-2 max-w-md mx-auto">
                            No subscription plans are available for consultants at this time. Please check back later.
                        </p>
                    </div>
                </div>
            </ConsultantLayout>
        );
    }

    return (
        <ConsultantLayout>
            <div className="min-h-screen bg-gray-50 pb-20">
                {/* Sticky Sub-Header */}
                <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
                    <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-blue-600" />
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-800">
                                Subscription & Services
                            </h1>
                        </div>

                        <button
                            onClick={() => navigate("/consultant/subscription-history")}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition"
                        >
                            <History className="h-4 w-4" />
                            View History
                        </button>
                    </div>
                </div>

                {/* Gradient Top Card */}
                <div className="mx-auto max-w-5xl px-4 mt-6">
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 sm:p-8 shadow-xl">
                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0 opacity-15"
                            style={{
                                backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
                                backgroundSize: "14px 14px",
                                color: "rgba(255,255,255,0.9)",
                                maskImage: "linear-gradient(to right, transparent, black 20%, black 80%, transparent)"
                            }}
                        />

                        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                            <div>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 font-medium">
                                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                                        Step 1: Review
                                    </span>
                                    <ChevronRight className="h-3.5 w-3.5 opacity-80" />
                                    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs opacity-80">
                                        Step 2: Pay Securely
                                    </span>
                                </div>

                                <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">
                                    Review & Complete Your Payment
                                </h2>

                                <div className="mt-4 flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1.5 text-sm font-semibold backdrop-blur-sm">
                                        <Tag className="h-4 w-4" />
                                        {offer.plan_name}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1.5 text-sm font-semibold backdrop-blur-sm">
                                        <Clock className="h-4 w-4" />
                                        {offer.duration_months} Month Plan
                                    </span>
                                </div>
                            </div>

                            <div className="rounded-2xl bg-white/10 p-5 backdrop-blur-md shadow-2xl border border-white/20 min-w-[200px]">
                                <div className="text-xs uppercase tracking-widest text-white/70 font-bold">
                                    Total Amount Due
                                </div>
                                <div className="mt-1 flex items-baseline gap-1">
                                    <span className="text-3xl font-black">{formatCurrency(total)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mx-auto max-w-5xl px-4 mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column: Plan Details */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                            <div className="p-6">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    <CheckCircle className="text-green-600 h-5 w-5" />
                                    Everything included in your plan
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {[
                                        "Visibility on consultant discovery page",
                                        "Direct contact from potential clients",
                                        "Profile verification badge",
                                        "Priority listing in search results",
                                        "Monthly performance insights",
                                        "24/7 Priority customer support"
                                    ].map((feature, idx) => (
                                        <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                                            <div className="bg-green-100 rounded-full p-1 text-green-700">
                                                <CheckCircle className="h-3.5 w-3.5" />
                                            </div>
                                            <span className="text-sm text-gray-700 font-medium">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Security Info */}
                        <div className="rounded-2xl border border-gray-200 bg-blue-50/50 p-6">
                            <div className="flex flex-wrap items-center gap-6 justify-center sm:justify-start">
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-600 uppercase tracking-tighter">
                                    <Lock className="h-4 w-4 text-blue-600" />
                                    Secure SSL
                                </div>
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-600 uppercase tracking-tighter">
                                    <BadgeCheck className="h-4 w-4 text-blue-600" />
                                    PCI Compliant
                                </div>
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-600 uppercase tracking-tighter">
                                    <ShieldCheck className="h-4 w-4 text-blue-600" />
                                    Powered by Cashfree
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden">
                            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                                <h3 className="font-bold text-gray-800">Order Summary</h3>
                            </div>

                            <div className="p-6 space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600 text-sm">Subscription Amount</span>
                                    <span className="text-gray-900 font-semibold">{formatCurrency(baseAmount)}</span>
                                </div>

                                <div className="space-y-2 pt-2 border-t border-gray-50">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-500 italic">
                                            CGST ({offer?.cgst_percentage ?? (offer?.gst_percentage ? offer.gst_percentage / 2 : 0)}%)
                                        </span>
                                        <span className="text-gray-600 font-medium">{formatCurrency(cgstAmount)}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-500 italic">
                                            SGST ({offer?.sgst_percentage ?? (offer?.gst_percentage ? offer.gst_percentage / 2 : 0)}%)
                                        </span>
                                        <span className="text-gray-600 font-medium">{formatCurrency(sgstAmount)}</span>
                                    </div>
                                    <div className="pt-2 mt-2 border-t border-gray-100">
                                        <p className="text-[10px] text-gray-400 italic">
                                            Billed to: Ducktail Private Limited
                                        </p>
                                        <p className="text-[10px] text-gray-400 italic">
                                            Tamilnadu - 627105
                                        </p>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-200">
                                    <div className="flex justify-between items-center">
                                        <span className="text-lg font-extrabold text-gray-800">Total Due</span>
                                        <span className="text-2xl font-black text-blue-600">{formatCurrency(total)}</span>
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-1 text-right">Includes all applicable taxes</p>
                                </div>

                                <button
                                    onClick={handlePayNow}
                                    disabled={isTransacting || total <= 0}
                                    className="w-full relative mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-4 shadow-lg shadow-blue-200 transition-all active:scale-95 disabled:opacity-70 disabled:grayscale disabled:cursor-not-allowed group"
                                >
                                    {isTransacting ? (
                                        <>
                                            <Loader2 className="animate-spin h-5 w-5" />
                                            Redirecting...
                                        </>
                                    ) : (
                                        <>
                                            <CreditCard className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                                            Pay Securely Now
                                            <ChevronRight className="h-4 w-4 ml-1" />
                                        </>
                                    )}
                                </button>

                                <div className="mt-6 flex flex-col items-center gap-2">
                                    <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4].map(i => (
                                            <div key={i} className="h-6 w-10 bg-gray-100 rounded-md border border-gray-200" />
                                        ))}
                                    </div>
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">100% Safe Payment</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ConsultantLayout>
    );
};

export default ConsultantSubscription;
